/* eslint-disable @typescript-eslint/no-unused-expressions */
import { GridColDef } from "@mui/x-data-grid-pro";
import {
  DeleteIcon,
  EditIcon,
  GKButton,
  GKClientDataGrid,
  Grid2,
  IconButton,
  PopConfirm,
  StatusField,
} from "components";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { EquityBasketListInterface } from "interfaces/EquityBasket";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "store/store";
import axiosInstance from "utils/axiosInstance";
import { UserRoleTypes } from "utils/common";
import { extraColumnsField } from "utils/helpers";
import { moneyCommaSeparator } from "utils/MathFunction";
import useDocumentTitle from "utils/useDocumentTitle";
import AddNewStockModal from "./AddNewStockModal";

const EquityBasketAssets = () => {
  useDocumentTitle("Basket Assets");

  const { id } = useParams();

  const userData = useAppSelector((state) => state?.user?.userData);

  /** ========================================================================================================= */
  const { data } = useFetchQuery({
    key: ["GET_EQUITY_BASKET_DETAILS_DATA"],
    route: `/model_portfolio/${id}/details/`,
    enabled: Boolean(id),
  });

  const basketDetails: EquityBasketListInterface = data?.data;

  const navigate = useNavigate();

  const [showStocksModal, setShowStocksModal] = useState(false);

  const [allStocks, setAllStocks] = useState([]);

  const [showConfirmationModal, setShowConfirmationModal] =
    useState<HTMLElement | null>(null);

  const [showConfirmationId, setShowConfirmationId] = useState(null);

  /** ========================================================================================================= */
  const { isFetching, refetch } = useFetchQuery({
    key: ["STOKE_UNIVERSE"],
    route: `model_portfolio/${id}/stock_universe/`,
    success: (response: any) => {
      setAllStocks(response?.data?.allocItem);
    },
  });

  /** ========================================================================================================= */
  const { mutate: handleDeleteStock } = useMutation({
    mutationFn: (data: { modelId: any; stockItemId: any }) =>
      axiosInstance?.delete(
        `/model_portfolio/${data?.modelId}/stock_item/${data?.stockItemId}/`,
      ),
    onSuccess: () => {
      refetch();
    },
  });

  const columns: GridColDef[] = [
    {
      ...extraColumnsField("Stocks", "companyName", 200, 1),
    },
    {
      ...extraColumnsField("Status", "assetStatus", 150, 1),
      renderCell: (params) => {
        return <StatusField statusString={params?.row?.assetStatus || "N/A"} />;
      },
    },
    {
      ...extraColumnsField("CMP", "cmp", 150, 1),
      valueFormatter: (params) => moneyCommaSeparator(params?.value || 0),
      type: "number",
    },
    {
      ...extraColumnsField("Target Price", "targetPrice", 150, 1),
      valueFormatter: (params) => moneyCommaSeparator(params?.value || 0),
      type: "number",
    },
    {
      ...extraColumnsField("Stop Loss", "stopLoss", 150, 1),
      valueFormatter: (params) => moneyCommaSeparator(params?.value || 0),
      type: "number",
    },
    {
      ...extraColumnsField("Portfolio Count", "portfolioCount", 150, 1),
      type: "number",
    },
    {
      ...extraColumnsField("Holding Value", "holdingValue", 150, 1),
      valueFormatter: (params) => moneyCommaSeparator(params?.value || 0),
      type: "number",
    },
    {
      ...extraColumnsField("Action", "action", 150, 1),
      align: "right",
      headerAlign: "right",
      renderCell: (params) => {
        return (
          <Grid2 container justifyContent={"flex-end"}>
            <IconButton
              className="outline"
              onClick={(event) => {
                event?.stopPropagation();
                navigate(
                  `/app/equity-basket/${id}/assets/${params?.row?.id}/edit`,
                );
              }}
            >
              <EditIcon height={18} width={18} />
            </IconButton>
            <IconButton
              color="error"
              className="outline"
              onClick={(event) => {
                event?.stopPropagation();
                setShowConfirmationModal(event.currentTarget);
                setShowConfirmationId(params.row.id);
              }}
            >
              <DeleteIcon width={18} />
            </IconButton>
          </Grid2>
        );
      },
    },
  ];

  return (
    <>
      <Grid2 container>
        <Grid2 size={12}>
          <GKClientDataGrid
            name="stockUniverse"
            maxHeight={`calc(100vh - 340px)`}
            loading={isFetching}
            rows={allStocks || []}
            onRowClick={(params) => {
              navigate(
                basketDetails?.managementStyle === "Subscription" &&
                  basketDetails?.basketType !== "Model"
                  ? `/app/equity-basket/${id}/recommendation/${params?.row?.id}/details`
                  : `/app/equity-basket/${id}/assets/${params?.row?.id}/details`,
              );
            }}
            headerComponent={
              userData?.userRole === UserRoleTypes.advisor && (
                <GKButton
                  variant="contained"
                  onClick={() => {
                    setShowStocksModal(true);
                  }}
                >
                  Add Stock
                </GKButton>
              )
            }
            pinnedColumns={{
              left: ["companyName"],
            }}
            columns={columns.filter((dataItem) =>
              userData?.userRole === UserRoleTypes.advisor
                ? dataItem
                : dataItem.field !== "action",
            )}
          />
        </Grid2>
      </Grid2>

      {showStocksModal && (
        <AddNewStockModal open={showStocksModal} setOpen={setShowStocksModal} />
      )}

      {showConfirmationModal && (
        <PopConfirm
          anchorEl={showConfirmationModal}
          open={Boolean(showConfirmationModal)}
          setOpen={setShowConfirmationModal}
          onSuccess={() => {
            handleDeleteStock({
              modelId: id,
              stockItemId: showConfirmationId,
            });
          }}
        />
      )}
    </>
  );
};

export default EquityBasketAssets;
